import { useCallback, useEffect, useState } from 'react';
import {
  MIGRATION_KIND_LABELS,
  type MigrationBatchDto,
  type MigrationIssue,
  type MigrationKind,
  type MigrationRowDto,
  type MigrationRowsResponse,
} from '@hesabat/contracts';
import { migrationApi } from './api';
import { Icon } from './icons';
import Loading from './Loading';

/**
 * منطقة الانتظار كجدول Excel: كل خلية تُعدَّل مكانها، وكل صف عليه مشكلة ملوّن بأشدها
 * (أحمر يمنع الاعتماد، أصفر يُراجع) ومعه إصلاح بضغطة حيث يُعرف الإصلاح. التحديد يفتح
 * التعديل الجماعى والحذف والدمج. الجدول الواسع يتمرر أفقيًا داخل إطاره على الموبايل.
 */
const PAGE = 200;

const FILTERS = [
  { value: 'all', label: 'كل الصفوف' },
  { value: 'issues', label: 'بها مشاكل' },
  { value: 'errors', label: 'أخطاء فقط' },
] as const;

/** حقول المبالغ والأكواد تُكتب من اليسار — الإشارة والفواصل فى مكانها. */
const LTR_FIELDS = new Set([
  'legacyCode',
  'accountCode',
  'targetCode',
  'newParentCode',
  'legacyParentCode',
  'debit',
  'credit',
  'balance',
  'taxNumber',
  'nationalId',
  'phone',
  'email',
  'paymentTermsDays',
  'balanceDate',
  'salePrice',
  'quantity',
  'unitCost',
  'totalValue',
]);

function severityOf(row: MigrationRowDto): 'error' | 'warning' | null {
  if (row.issues.some((issue) => issue.severity === 'error')) return 'error';
  if (row.issues.length > 0) return 'warning';
  return null;
}

export default function MigrationReview({
  base,
  batchId,
  counts,
  focus,
  onChanged,
  onError,
}: {
  base: string;
  batchId: string;
  counts: MigrationBatchDto['counts'];
  focus: { kind: MigrationKind; rowId: string } | null;
  onChanged: () => Promise<void>;
  onError: (caught: unknown) => void;
}) {
  const available = counts.filter((count) => count.rows > 0);
  const [kind, setKind] = useState<MigrationKind>(
    focus?.kind ?? available[0]?.kind ?? 'trial_balance',
  );
  const [filter, setFilter] = useState<string>(focus ? 'all' : 'issues');
  const [query, setQuery] = useState('');
  const [offset, setOffset] = useState(0);
  const [page, setPage] = useState<MigrationRowsResponse | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkField, setBulkField] = useState('');
  const [bulkValue, setBulkValue] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setPage(
        await migrationApi.rows(base, batchId, {
          kind,
          filter,
          q: query || undefined,
          offset,
          limit: PAGE,
        }),
      );
    } catch (caught) {
      onError(caught);
    }
  }, [base, batchId, kind, filter, query, offset, onError]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setSelected(new Set());
  }, [kind, filter, query]);

  // الصف المشتبه من المطابقة المحاسبية: يُعرض ويُبرز
  useEffect(() => {
    if (!focus || !page) return;
    document.getElementById('mig-row-' + focus.rowId)?.scrollIntoView({ block: 'center' });
  }, [focus, page]);

  async function run(action: () => Promise<unknown>): Promise<void> {
    setBusy(true);
    try {
      await action();
      setSelected(new Set());
      await Promise.all([load(), onChanged()]);
    } catch (caught) {
      onError(caught);
    } finally {
      setBusy(false);
    }
  }

  const patch = (rowId: string, data: Record<string, string>) =>
    run(() => migrationApi.updateRows(base, batchId, { updates: [{ rowId, data }] }));

  function applyFix(row: MigrationRowDto, issue: MigrationIssue): void {
    const fix = issue.fix;
    if (!fix) return;
    if (fix.action === 'patch' && fix.patch) void patch(row.id, fix.patch);
    else if (fix.action === 'delete')
      void run(() => migrationApi.deleteRows(base, batchId, [row.id]));
    else if (fix.action === 'merge' && fix.mergeWith?.length) {
      void run(() => migrationApi.mergeRows(base, batchId, row.id, fix.mergeWith!));
    }
  }

  function acknowledge(row: MigrationRowDto, issue: MigrationIssue): void {
    const acks = new Set((row.data._ack ?? '').split(',').filter(Boolean));
    acks.add(issue.code);
    void patch(row.id, { _ack: [...acks].join(',') });
  }

  const fields = page?.fields ?? [];
  const showResolved = kind === 'trial_balance' || kind === 'accounts';
  const allSelected =
    page !== null && page.rows.length > 0 && page.rows.every((row) => selected.has(row.id));

  return (
    <div className="mig-step mig-review">
      <div className="tabs" role="tablist">
        {(available.length ? available : counts.slice(0, 1)).map((count) => (
          <button
            key={count.kind}
            type="button"
            role="tab"
            aria-selected={kind === count.kind}
            className={'tab' + (kind === count.kind ? ' active' : '')}
            onClick={() => {
              setKind(count.kind);
              setOffset(0);
            }}
          >
            {MIGRATION_KIND_LABELS[count.kind]} <span className="mig-count">{count.rows}</span>
            {count.errors > 0 && <span className="mig-badge error">{count.errors}</span>}
            {count.errors === 0 && count.warnings > 0 && (
              <span className="mig-badge warning">{count.warnings}</span>
            )}
          </button>
        ))}
      </div>

      <div className="mig-toolbar">
        <select
          aria-label="عرض"
          value={filter}
          onChange={(event) => {
            setFilter(event.target.value);
            setOffset(0);
          }}
        >
          {FILTERS.map((item) => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
        </select>
        <input
          type="search"
          placeholder="بحث فى الصفوف…"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setOffset(0);
          }}
        />
        {page && (
          <span className="sub">
            {page.summary.rows} صف —{' '}
            <span className="mig-text-error">{page.summary.rowsWithErrors} بها أخطاء</span>
            {page.summary.warnings > 0 && <> — {page.summary.warnings} تنبيه</>}
          </span>
        )}
      </div>

      {selected.size > 0 && (
        <div className="mig-bulk" role="region" aria-label="تعديل جماعى">
          <strong>{selected.size} محدد</strong>
          <select
            value={bulkField}
            onChange={(event) => setBulkField(event.target.value)}
            aria-label="الحقل"
          >
            <option value="">اختر حقلاً…</option>
            {fields.map((field) => (
              <option key={field.key} value={field.key}>
                {field.label}
              </option>
            ))}
          </select>
          <input
            value={bulkValue}
            onChange={(event) => setBulkValue(event.target.value)}
            placeholder="القيمة الجديدة"
            aria-label="القيمة"
          />
          <button
            type="button"
            className="primary"
            disabled={!bulkField || busy}
            onClick={() =>
              void run(() =>
                migrationApi.updateRows(base, batchId, {
                  updates: [...selected].map((rowId) => ({
                    rowId,
                    data: { [bulkField]: bulkValue },
                  })),
                }),
              )
            }
          >
            طبّق على المحدد
          </button>
          {selected.size > 1 && (
            <button
              type="button"
              className="ghost"
              disabled={busy}
              onClick={() => {
                const [keep, ...rest] = page!.rows
                  .filter((row) => selected.has(row.id))
                  .map((row) => row.id);
                if (
                  keep &&
                  window.confirm('دمج ' + selected.size + ' صفوف فى الأول منها بجمع أرصدتها؟')
                ) {
                  void run(() => migrationApi.mergeRows(base, batchId, keep, rest));
                }
              }}
            >
              ادمجها
            </button>
          )}
          <button
            type="button"
            className="link"
            disabled={busy}
            onClick={() => {
              if (window.confirm('استبعاد ' + selected.size + ' صفوف من الدفعة؟')) {
                void run(() => migrationApi.deleteRows(base, batchId, [...selected]));
              }
            }}
          >
            استبعد المحدد
          </button>
        </div>
      )}

      {!page ? (
        <Loading />
      ) : page.rows.length === 0 ? (
        <p className="success mig-none">
          <Icon name="check" size={18} /> {filter === 'all' ? 'لا صفوف.' : 'لا مشاكل فى هذا النوع.'}
        </p>
      ) : (
        <div className="table-scroll mig-grid-wrap">
          <table className="mig-grid">
            <thead>
              <tr>
                <th className="mig-sticky">
                  <input
                    type="checkbox"
                    aria-label="تحديد الكل"
                    checked={allSelected}
                    onChange={() =>
                      setSelected(allSelected ? new Set() : new Set(page.rows.map((row) => row.id)))
                    }
                  />
                </th>
                <th>صف</th>
                {showResolved && <th>يُرحَّل على</th>}
                {fields.map((field) => (
                  <th key={field.key} className={field.review ? 'mig-review-col' : ''}>
                    {field.label}
                    {field.required && ' *'}
                  </th>
                ))}
                <th className="mig-issues-col">المشاكل والإصلاح</th>
              </tr>
            </thead>
            <tbody>
              {page.rows.map((row) => {
                const severity = severityOf(row);
                return (
                  <tr
                    key={row.id}
                    id={'mig-row-' + row.id}
                    className={
                      (severity ? 'mig-' + severity : '') +
                      (focus?.rowId === row.id ? ' mig-focus' : '')
                    }
                  >
                    <td className="mig-sticky">
                      <input
                        type="checkbox"
                        aria-label={'تحديد الصف ' + row.rowNumber}
                        checked={selected.has(row.id)}
                        onChange={() => {
                          const next = new Set(selected);
                          if (next.has(row.id)) next.delete(row.id);
                          else next.add(row.id);
                          setSelected(next);
                        }}
                      />
                    </td>
                    <td className="num-cell">
                      {row.rowNumber}
                      {row.edited && (
                        <span className="mig-edited" title="عُدِّل يدويًا">
                          ✎
                        </span>
                      )}
                    </td>
                    {showResolved && (
                      <td className="mig-resolved">
                        {row.resolved ? (
                          <>
                            <span className="mig-ltr">{row.resolved.code}</span>{' '}
                            {row.resolved.nameAr}
                          </>
                        ) : (
                          <span className="mig-text-error">بلا مقابل</span>
                        )}
                        {row.suggestions.length > 0 && (
                          <span className="mig-suggestions">
                            {row.suggestions.map((suggestion) => (
                              <button
                                key={suggestion.code}
                                type="button"
                                className="mig-chip"
                                disabled={busy}
                                title={'تشابه ' + suggestion.score + '٪'}
                                onClick={() => void patch(row.id, { targetCode: suggestion.code })}
                              >
                                {suggestion.code} {suggestion.nameAr}
                              </button>
                            ))}
                          </span>
                        )}
                      </td>
                    )}
                    {fields.map((field) => {
                      const flagged = row.issues.find((issue) => issue.field === field.key);
                      return (
                        <td
                          key={field.key}
                          className={flagged ? 'mig-cell-' + flagged.severity : ''}
                        >
                          <input
                            key={row.id + field.key + (row.data[field.key] ?? '')}
                            className={'mig-cell' + (LTR_FIELDS.has(field.key) ? ' mig-ltr' : '')}
                            defaultValue={row.data[field.key] ?? ''}
                            aria-label={field.label + ' — صف ' + row.rowNumber}
                            disabled={busy}
                            onBlur={(event) => {
                              const value = event.target.value;
                              if (value !== (row.data[field.key] ?? ''))
                                void patch(row.id, { [field.key]: value });
                            }}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter') (event.target as HTMLInputElement).blur();
                            }}
                          />
                        </td>
                      );
                    })}
                    <td className="mig-issues">
                      {row.issues.map((issue, index) => (
                        <div key={index} className={'mig-issue ' + issue.severity}>
                          <span>{issue.message}</span>
                          {issue.fix && (
                            <button
                              type="button"
                              className="mig-fix"
                              disabled={busy}
                              onClick={() => applyFix(row, issue)}
                            >
                              {issue.fix.label}
                            </button>
                          )}
                          {(issue.severity === 'warning' || issue.code === 'SIMILAR_NAME') && (
                            <button
                              type="button"
                              className="link"
                              disabled={busy}
                              onClick={() => acknowledge(row, issue)}
                            >
                              {issue.code === 'SIMILAR_NAME' ? 'ليسا نفس الطرف' : 'تجاهل'}
                            </button>
                          )}
                        </div>
                      ))}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {page && page.total > PAGE && (
        <div className="form-actions">
          <button
            type="button"
            className="ghost"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE))}
          >
            السابق
          </button>
          <span className="sub">
            {offset + 1}–{Math.min(offset + PAGE, page.total)} من {page.total}
          </span>
          <button
            type="button"
            className="ghost"
            disabled={offset + PAGE >= page.total}
            onClick={() => setOffset(offset + PAGE)}
          >
            التالى
          </button>
        </div>
      )}
    </div>
  );
}
